/**
 * The DAG itself: storage, tip selection, and cumulative weight.
 *
 * Why a DAG and not a chain. A chain forces every transaction in the world
 * through one slot at a time, so throughput is capped by whatever the
 * slowest agreed-upon block cadence is, and adding nodes makes it slower,
 * not faster. In a DAG every new vertex references and thereby confirms
 * several earlier ones, so writes proceed in parallel and confirmation
 * work is done by the traffic itself. Two things follow directly, and they
 * are exactly the two properties the network is supposed to have:
 *
 *   - Security grows with use. A vertex's cumulative weight is the number
 *     of later vertices that transitively reference it. Rewriting history
 *     means out-running every one of them, so the cost of reversal climbs
 *     with every transaction anyone anywhere makes.
 *   - Speed grows with participation. There is no global ordering
 *     bottleneck to contend for; more issuers means a wider frontier of
 *     tips, more parallel attachment, and more confirmations per second.
 */

import { toHex } from './bytes.ts';
import { tag } from './hash.ts';
import { params, type ChainParams } from './params.ts';
import { conflictKey, type SignedVertex, type VertexId } from './tx.ts';
import { utf8 } from './bytes.ts';

export type VertexStatus = 'pending' | 'accepted' | 'rejected';

export const GENESIS_ANCHORS = 4;

/** Deterministic empty vertices that seed the frontier so the very first
 *  real transaction has parents to reference. They carry no value. */
export function genesisAnchors(p: ChainParams = params()): VertexId[] {
  const out: VertexId[] = [];
  for (let i = 0; i < GENESIS_ANCHORS; i++) {
    out.push(toHex(tag('face/genesis/v1', utf8(p.chainId), new Uint8Array([i]))));
  }
  return out;
}

export interface DagEntry {
  vertex: SignedVertex;
  status: VertexStatus;
  /** Number of later vertices that transitively reference this one. */
  weight: number;
  /** Wall-clock arrival, local only, never signed or gossiped. */
  seenAt: number;
}

/** How far back a new arrival propagates its weight. Bounds the work per
 *  insert; deep history is already far past any reversal threshold. */
const WEIGHT_PROPAGATION_CAP = 4096;

export class Dag {
  private entries = new Map<VertexId, DagEntry>();
  private childrenOf = new Map<VertexId, Set<VertexId>>();
  private tipSet = new Set<VertexId>();
  private conflicts = new Map<string, Set<VertexId>>();
  private anchors: Set<VertexId>;
  readonly chainParams: ChainParams;

  constructor(p: ChainParams = params()) {
    this.chainParams = p;
    this.anchors = new Set(genesisAnchors(p));
    for (const a of this.anchors) this.tipSet.add(a);
  }

  get size(): number {
    return this.entries.size;
  }

  isAnchor(id: VertexId): boolean {
    return this.anchors.has(id);
  }

  has(id: VertexId): boolean {
    return this.entries.has(id) || this.anchors.has(id);
  }

  get(id: VertexId): DagEntry | undefined {
    return this.entries.get(id);
  }

  vertex(id: VertexId): SignedVertex | undefined {
    return this.entries.get(id)?.vertex;
  }

  status(id: VertexId): VertexStatus | undefined {
    if (this.anchors.has(id)) return 'accepted';
    return this.entries.get(id)?.status;
  }

  children(id: VertexId): VertexId[] {
    return [...(this.childrenOf.get(id) ?? [])];
  }

  entriesList(): DagEntry[] {
    return [...this.entries.values()];
  }

  /** True when every parent is already known - a vertex is not insertable
   *  until its whole causal past is, which is what keeps the DAG closed. */
  parentsKnown(v: SignedVertex): boolean {
    return v.parents.every((p) => this.has(p));
  }

  missingParents(v: SignedVertex): VertexId[] {
    return v.parents.filter((p) => !this.has(p));
  }

  insert(v: SignedVertex, status: VertexStatus = 'pending', seenAt = Date.now()): DagEntry {
    const existing = this.entries.get(v.id);
    if (existing) return existing;

    const entry: DagEntry = { vertex: v, status, weight: 0, seenAt };
    this.entries.set(v.id, entry);
    this.tipSet.add(v.id);

    for (const p of v.parents) {
      let set = this.childrenOf.get(p);
      if (!set) this.childrenOf.set(p, (set = new Set()));
      set.add(v.id);
      this.tipSet.delete(p);
    }

    const key = conflictKey(v);
    let cset = this.conflicts.get(key);
    if (!cset) this.conflicts.set(key, (cset = new Set()));
    cset.add(v.id);

    this.propagateWeight(v);
    return entry;
  }

  /** Every vertex the new arrival transitively confirms gets +1 weight. */
  private propagateWeight(v: SignedVertex): void {
    const seen = new Set<VertexId>();
    const queue = [...v.parents];
    let visited = 0;
    while (queue.length && visited < WEIGHT_PROPAGATION_CAP) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      visited++;
      const e = this.entries.get(id);
      if (!e) continue; // anchor
      e.weight++;
      for (const p of e.vertex.parents) if (!seen.has(p)) queue.push(p);
    }
  }

  setStatus(id: VertexId, status: VertexStatus): void {
    const e = this.entries.get(id);
    if (e) e.status = status;
  }

  /** Ids sharing this vertex's conflict set, itself excluded. */
  conflictSet(v: SignedVertex): VertexId[] {
    const set = this.conflicts.get(conflictKey(v));
    if (!set) return [];
    return [...set].filter((id) => id !== v.id);
  }

  conflictSetByKey(key: string): VertexId[] {
    return [...(this.conflicts.get(key) ?? [])];
  }

  /* ---------------------------- tips ------------------------------ */

  tips(): VertexId[] {
    return [...this.tipSet];
  }

  /**
   * Tip selection. Prefer tips that are already preferred and heavy, break
   * ties at random so honest issuers spread across the frontier instead of
   * piling onto one vertex. Lazy tips - old ones nobody has confirmed -
   * are skipped, which is what stops a parasite chain from accumulating
   * weight by only ever referencing its own ancestors.
   */
  selectTips(count: number, isPreferred: (id: VertexId) => boolean, now = Date.now()): VertexId[] {
    const p = this.chainParams;
    const want = Math.max(p.minParents, Math.min(p.maxParents, count));
    const candidates = this.tips().filter((id) => {
      if (this.anchors.has(id)) return true;
      const e = this.entries.get(id);
      if (!e || e.status === 'rejected') return false;
      return isPreferred(id);
    });

    const pool = candidates.length ? candidates : [...this.anchors];
    const scored = pool.map((id) => {
      const e = this.entries.get(id);
      const age = e ? Math.max(1, now - e.seenAt) : 1;
      // Weight rewards confirmed work; the age term keeps the frontier
      // fresh; the jitter keeps honest issuers from colliding.
      const score = (1 + (e?.weight ?? 0)) * (1 + 1000 / age) * (0.75 + Math.random() * 0.5);
      return { id, score };
    });
    scored.sort((a, b) => b.score - a.score);

    const picked: VertexId[] = [];
    for (const s of scored) {
      if (picked.length >= want) break;
      picked.push(s.id);
    }
    // Pad from the anchors if the frontier is still narrower than minParents.
    for (const a of this.anchors) {
      if (picked.length >= want) break;
      if (!picked.includes(a)) picked.push(a);
    }
    return picked.slice(0, want).sort();
  }

  /* -------------------------- traversal --------------------------- */

  ancestors(id: VertexId, limit = 4096): Set<VertexId> {
    const out = new Set<VertexId>();
    const queue = [id];
    while (queue.length && out.size < limit) {
      const cur = queue.shift()!;
      const e = this.entries.get(cur);
      if (!e) continue;
      for (const p of e.vertex.parents) {
        if (out.has(p)) continue;
        out.add(p);
        queue.push(p);
      }
    }
    return out;
  }

  /** Parents first. Used to replay a subgraph into ledger state in order. */
  topologicalOrder(ids: Iterable<VertexId>): VertexId[] {
    const wanted = new Set(ids);
    const out: VertexId[] = [];
    const state = new Map<VertexId, 0 | 1 | 2>();
    const visit = (id: VertexId): void => {
      if (!wanted.has(id) || state.get(id) === 2) return;
      if (state.get(id) === 1) return; // cycle guard; DAG shape forbids it
      state.set(id, 1);
      const e = this.entries.get(id);
      if (e) for (const p of e.vertex.parents) visit(p);
      state.set(id, 2);
      out.push(id);
    };
    for (const id of wanted) visit(id);
    return out;
  }

  /**
   * Confirmation confidence of an accepted vertex, as the share of the
   * live frontier that transitively rests on it. 1.0 means every current
   * tip confirms it and reversal would mean rewriting the whole frontier.
   */
  confirmationRatio(id: VertexId): number {
    const tips = this.tips().filter((t) => !this.anchors.has(t));
    if (!tips.length) return 0;
    let confirming = 0;
    for (const t of tips) {
      if (t === id || this.ancestors(t, 2048).has(id)) confirming++;
    }
    return confirming / tips.length;
  }

  stats(): { vertices: number; tips: number; accepted: number; pending: number; rejected: number; totalWeight: number } {
    let accepted = 0, pending = 0, rejected = 0, totalWeight = 0;
    for (const e of this.entries.values()) {
      if (e.status === 'accepted') accepted++;
      else if (e.status === 'pending') pending++;
      else rejected++;
      totalWeight += e.weight;
    }
    return { vertices: this.entries.size, tips: this.tipSet.size, accepted, pending, rejected, totalWeight };
  }
}
